import React from 'react';

import {
  EXPORT_AUDIT_LOG_DISPLAY_STRING,
  EXPORT_RESULT_AUDIT_LOG_DISPLAY_STRING,
  EXPORT_ONLY_AUDIT_LOG_DISPLAY_STRING,
  EXPORT_ONLY_RESULT_AUDIT_LOG_DISPLAY_STRING,
} from 'audit/AuditConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';

import * as AuditActions from './AuditActions';
import type {AuditLogViewModalAdditionalOptions} from './view-modal/AuditLogViewModal';

interface ExportAuditLogButtonProps {
  buttonStyle: ButtonStyle;
  className?: string;
  ids: Array<string>;
  additionalOptions?: AuditLogViewModalAdditionalOptions;
  buttonSize?: ButtonSize;
  customHandleClick?: () => void;
  /**
   * Whether this button supports viewing audit logs within the UI. When false, tooltip will show export-only
   * text. Viewing is unsupported when the audit log requires a custom export endpoint (e.g. TAC reports).
   */
  isViewingSupported?: boolean;
  /** Overrides the default tooltip and accessible label, e.g. when the button covers several audit logs. */
  tooltipText?: string;
}

class ExportAuditLogButton extends React.Component<ExportAuditLogButtonProps> {
  static defaultProps = {
    buttonStyle: ButtonStyle.TERTIARY_BLUE,
    buttonSize: ButtonSize.SMALL,
    ids: [],
    isViewingSupported: true,
  };

  private handleClick = (): void => {
    const {customHandleClick, ids, additionalOptions} = this.props;
    if (customHandleClick) {
      customHandleClick();
    } else {
      AuditActions.exportAuditLogs(ids, additionalOptions ?? {});
    }
  };

  private isDisabled = (): boolean => {
    const {customHandleClick, ids, additionalOptions: opts} = this.props;
    const hasExportTarget =
      Boolean(customHandleClick) ||
      ids.length > 0 ||
      opts?.reportType != null ||
      Boolean(opts?.relatedResultsModelId) ||
      Boolean(opts?.resultsWarehouseIds?.length);
    return !hasExportTarget;
  };

  private getTooltipTitle = (): string => {
    const {additionalOptions, isViewingSupported, tooltipText} = this.props;
    if (tooltipText != null) {
      return tooltipText;
    }

    const isResultsAuditLog =
      additionalOptions?.resultsWarehouseIds || additionalOptions?.relatedResultsModelId;

    // Certain custom export handlers (e.g., TAC reports) only support file downloads and do not support
    // displaying audit logs within the UI.
    if (isViewingSupported) {
      return isResultsAuditLog ? EXPORT_RESULT_AUDIT_LOG_DISPLAY_STRING : EXPORT_AUDIT_LOG_DISPLAY_STRING;
    } else {
      return isResultsAuditLog
        ? EXPORT_ONLY_RESULT_AUDIT_LOG_DISPLAY_STRING
        : EXPORT_ONLY_AUDIT_LOG_DISPLAY_STRING;
    }
  };

  override render(): JSX.Element {
    const title = this.getTooltipTitle();
    return (
      <Tooltip placement={FloatingPlacement.TOP} text={title}>
        <Button
          aria-label={title}
          buttonStyle={this.props.buttonStyle}
          className={this.props.className}
          disabled={this.isDisabled()}
          icon="audit-trail"
          onClick={this.handleClick}
          size={this.props.buttonSize}
        />
      </Tooltip>
    );
  }
}

export default ExportAuditLogButton;
